// app/javascript/controllers/funnel_step_form_controller.js

import { Controller } from "@hotwired/stimulus"

/**
* Funnel Step Form Controller
* Handles dynamic form behavior for funnel step creation/editing
* - Product selection with visual feedback
* - Dynamic order bump management
* - Page type conditional field visibility
* - 🆕 Landing page source configuration
* - 🆕 Button destination configuration
* - Form validation before submission
*/
export default class extends Controller {
  static targets = [
    "pageTypeSelect",
    "productSection",
    "orderBumpCheckbox",
    "orderBumpConfig",
    "successRedirectConfig",
    "customSuccessUrlInput",
    "passwordSetupOption",
    "productCount",
    "selectedCount",

    // Landing page source targets
    "landingSourceSection",
    "landingSourceRadio",
    "internalPageSelector",
    "externalUrlInput",
    "nextStepUrl",

    // Button destination targets
    "buttonDestinationSection",
    "specificStepSelector",
    "internalPageRedirectSelector",
    "externalLandingMessage",

    // Title update targets
    "redirectSectionTitle",
    "redirectSectionDescription",

    // URL auto-generation targets
    "stepNameInput",
    "urlPathInput",

    // Product search
    "productSearchInput",

    // Checkout fields section
    "checkoutFieldsSection"
  ]



  // 🆕 ADD THIS MISSING METHOD
  connect() {
    console.log("🎯 Funnel Step Form Controller connected!")

    requestAnimationFrame(() => {
      this.initializeForm()
      this.initializeProductCards()
      this.initializePageType()
    })
  }



  disconnect() {
    if (this.initializeTimeout) {
      clearTimeout(this.initializeTimeout)
    }
    console.log("👋 Funnel Step Form Controller disconnected")
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  initializeForm() {
    if (this.hasOrderBumpCheckboxTarget && this.hasOrderBumpConfigTarget) {
      const isChecked = this.orderBumpCheckboxTarget.checked
      this.orderBumpConfigTarget.style.display = isChecked ? 'block' : 'none'
      console.log(`Order bump config initialized: ${isChecked ? 'visible' : 'hidden'}`)
    }
  }

  initializeProductCards() {
    console.log("🔄 Initializing product cards...")

    const productCards = document.querySelectorAll('.product-card')
    console.log(`Found ${productCards.length} product cards`)

    productCards.forEach((card) => {
      const checkbox = card.querySelector('input[type="checkbox"]')
      if (checkbox?.checked) {
        this.selectProductCard(card)
      }
    })
  }

  initializePageType() {
    this.pageTypeChanged()
  }

  // ============================================
  // URL PATH AUTO-GENERATION
  // ============================================

  stepNameChanged(event) {
    const stepName = event.target.value

    if (!stepName || !this.hasUrlPathInputTarget) {
      return
    }

    // Don't auto-generate if user has manually edited the URL
    if (this.urlPathManuallyEdited) {
      return
    }

    const slug = this.generateSlug(stepName)
    this.urlPathInputTarget.value = slug

    console.log(`🔗 Auto-generated URL path: ${slug}`)
  }

  urlPathManuallyChanged() {
    this.urlPathManuallyEdited = true
    console.log("⚠️ URL path manually edited - auto-generation disabled")
  }

  generateSlug(text) {
    return text
      .toLowerCase()                          // Convert to lowercase
      .trim()                                 // Remove leading/trailing spaces
      .replace(/[àáâãäå]/g, 'a')             // Replace accented characters
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[ñ]/g, 'n')
      .replace(/[ç]/g, 'c')
      .replace(/[^a-z0-9\s-]/g, '')          // Remove invalid chars
      .replace(/\s+/g, '-')                   // Replace spaces with -
      .replace(/-+/g, '-')                    // Replace multiple - with single -
      .replace(/^-+/, '')                     // Trim - from start
      .replace(/-+$/, '')                     // Trim - from end
  }



  // ============================================
  // PRODUCT SELECTION (List-based design)
  // ============================================

  productCheckboxChanged(event) {
    const checkbox = event.target
    const label = checkbox.closest('.product-item')

    if (checkbox.checked) {
      label.classList.add('bg-beb-success-soft')
      label.classList.remove('bg-beb-surface')
    } else {
      label.classList.remove('bg-beb-success-soft')
      label.classList.add('bg-beb-surface')
    }

    this.updateProductCount()
    console.log(`Product ${checkbox.value} ${checkbox.checked ? 'selected' : 'deselected'}`)
  }

  // Legacy card-based selection (backwards compatibility)
  productCardClicked(event) {
    event.preventDefault()
    event.stopPropagation()

    const card = event.currentTarget
    const checkbox = card.querySelector('input[type="checkbox"]')

    if (!checkbox) {
      console.warn("No checkbox found in product card")
      return
    }

    checkbox.checked = !checkbox.checked
    checkbox.dispatchEvent(new Event('change', { bubbles: true }))

    if (checkbox.checked) {
      this.selectProductCard(card)
    } else {
      this.deselectProductCard(card)
    }

    this.updateProductCount()
    console.log(`Product ${checkbox.value} ${checkbox.checked ? 'selected' : 'deselected'}`)
  }

  selectProductCard(card) {
    card.classList.remove('border-beb-line', 'bg-beb-surface')
    card.classList.add('border-beb-success', 'bg-beb-success-soft', 'shadow-md')
    card.style.backgroundColor = '#f0fdf4'
  }

  deselectProductCard(card) {
    card.classList.remove('border-beb-success', 'bg-beb-success-soft', 'shadow-md')
    card.classList.add('border-beb-line', 'bg-beb-surface')
    card.style.backgroundColor = 'white'
  }

  updateProductCount() {
    if (!this.hasSelectedCountTarget) return

    const selectedCount = document.querySelectorAll('input[name="product_ids[]"]:checked').length
    this.selectedCountTarget.textContent = selectedCount
    console.log(`✅ Product count updated: ${selectedCount}`)
  }

  // Search/filter products
  filterProducts(event) {
    const query = event.target.value.toLowerCase().trim()
    const productItems = this.element.querySelectorAll('.product-item')

    productItems.forEach(item => {
      const title = item.dataset.productTitle || ''
      if (query === '' || title.includes(query)) {
        item.style.display = ''
      } else {
        item.style.display = 'none'
      }
    })

    console.log(`🔍 Filtering products by: "${query}"`)
  }

  // ============================================
  // 🆕 NEW: LANDING PAGE SOURCE HANDLING
  // ============================================

  landingSourceChanged(event) {
    const sourceType = event.target.value
    console.log(`📄 Landing source changed to: ${sourceType}`)

    // Hide all conditional sections first
    if (this.hasInternalPageSelectorTarget) {
      this.internalPageSelectorTarget.style.display = 'none'
    }
    if (this.hasExternalUrlInputTarget) {
      this.externalUrlInputTarget.style.display = 'none'
    }

    // Show the appropriate section
    switch (sourceType) {
      case 'internal_page':
        if (this.hasInternalPageSelectorTarget) {
          this.internalPageSelectorTarget.style.display = 'block'
        }
        this.showButtonDestination()
        break

      case 'external_url':
        if (this.hasExternalUrlInputTarget) {
          this.externalUrlInputTarget.style.display = 'block'
        }
        this.hideButtonDestination()
        break

      case 'custom_template':
        this.showButtonDestination()
        break
    }
  }

  showButtonDestination() {
    if (this.hasButtonDestinationSectionTarget) {
      this.buttonDestinationSectionTarget.style.display = 'block'
    }
    if (this.hasExternalLandingMessageTarget) {
      this.externalLandingMessageTarget.style.display = 'none'
    }
    console.log("✅ Button destination section shown")
  }

  hideButtonDestination() {
    if (this.hasButtonDestinationSectionTarget) {
      this.buttonDestinationSectionTarget.style.display = 'none'
    }
    if (this.hasExternalLandingMessageTarget) {
      this.externalLandingMessageTarget.style.display = 'block'
    }
    console.log("⚠️ Button destination section hidden (external landing)")
  }

  copyNextStepUrl(event) {
    event.preventDefault()

    if (!this.hasNextStepUrlTarget) return

    const url = this.nextStepUrlTarget.value

    navigator.clipboard.writeText(url).then(() => {
      const button = event.currentTarget
      const originalText = button.innerHTML

      button.innerHTML = '✓ Copied!'
      button.classList.add('bg-beb-success')
      button.classList.remove('bg-beb-warn')

      setTimeout(() => {
        button.innerHTML = originalText
        button.classList.remove('bg-beb-success')
        button.classList.add('bg-beb-warn')
      }, 2000)

      console.log("📋 URL copied to clipboard")
    }).catch(err => {
      console.error("Failed to copy URL:", err)
      alert("Copie impossible. Copiez l'adresse à la main.")
    })
  }

  // ============================================
  // PAGE TYPE HANDLING
  // ============================================

  pageTypeChanged() {
    // Get the CHECKED radio button, not just the first target
    const checkedRadio = this.element.querySelector('input[name="funnel_step[page_type]"]:checked')
    const pageType = checkedRadio ? checkedRadio.value : 'landing'
    console.log(`📄 Page type changed to: ${pageType}`)

    const orderBumpParent = this.orderBumpCheckboxTarget?.closest('.bg-white.rounded-xl.shadow-sm')

    this.hideAllOptionalSections()

    switch (pageType) {
      case 'landing':
        console.log("✅ Landing page: show landing source + redirect")
        if (this.hasLandingSourceSectionTarget) {
          this.landingSourceSectionTarget.style.display = 'block'
        }
        this.showSection(this.successRedirectConfigTarget)
        this.hidePasswordSetupOption()
        this.hideCheckoutFields()
        if (orderBumpParent) orderBumpParent.style.display = 'none'

        // 🆕 FIX: Update title for landing pages
        if (this.hasRedirectSectionTitleTarget) {
          this.redirectSectionTitleTarget.textContent = "Button Destination"
        }
        if (this.hasRedirectSectionDescriptionTarget) {
          this.redirectSectionDescriptionTarget.textContent = "Where do call-to-action buttons take users?"
        }

        // Check if external landing is selected
        const selectedSource = document.querySelector('input[name="funnel_step[landing_source_type]"]:checked')
        if (selectedSource?.value === 'external_url') {
          this.hideButtonDestination()
        }
        break


      case 'checkout':
      case 'oto':
        console.log(`✅ ${pageType} page: products + order bumps + redirect`)
        if (this.hasLandingSourceSectionTarget) {
          this.landingSourceSectionTarget.style.display = 'none'
        }
        this.showSection(this.productSectionTarget)
        this.showSection(this.successRedirectConfigTarget)
        this.showCheckoutFields()
        this.showPasswordSetupOption()
        this.showButtonDestination()
        if (orderBumpParent) orderBumpParent.style.display = 'block'
        this.syncOrderBumpConfigVisibility()

        // 🆕 FIX: Update title for checkout/OTO pages
        if (this.hasRedirectSectionTitleTarget) {
          this.redirectSectionTitleTarget.textContent = "After Payment Success"
        }
        if (this.hasRedirectSectionDescriptionTarget) {
          this.redirectSectionDescriptionTarget.textContent = "Where users go after completing payment"
        }
        break

      default:
        console.warn(`Unknown page type: ${pageType}`)
    }
  }

  hideAllOptionalSections() {
    const sections = [
      this.productSectionTarget,
      this.successRedirectConfigTarget
    ].filter(Boolean)

    sections.forEach(section => this.hideSection(section))
  }


  showSection(section) {
    if (!section) return

    section.style.display = 'block'

    if (section !== this.orderBumpConfigTarget) {
      const inputs = section.querySelectorAll('input, select, textarea')
      inputs.forEach(input => input.disabled = false)
    }
  }

  hideSection(section) {
    if (!section) return

    section.style.display = 'none'

    const inputs = section.querySelectorAll('input, select, textarea')
    inputs.forEach(input => {
      input.disabled = true
      input.removeAttribute('required')
    })
  }

  syncOrderBumpConfigVisibility() {
    if (this.hasOrderBumpCheckboxTarget && this.hasOrderBumpConfigTarget) {
      const isChecked = this.orderBumpCheckboxTarget.checked
      this.orderBumpConfigTarget.style.display = isChecked ? 'block' : 'none'
      console.log(`Order bump config synced: ${isChecked ? 'enabled' : 'disabled'}`)
    }
  }

  hidePasswordSetupOption() {
    if (!this.hasPasswordSetupOptionTarget) return

    this.passwordSetupOptionTarget.style.display = 'none'

    const nextStepRadio = this.element.querySelector('input[value="next_step"]')
    if (nextStepRadio) nextStepRadio.checked = true
  }

  showPasswordSetupOption() {
    if (!this.hasPasswordSetupOptionTarget) return
    this.passwordSetupOptionTarget.style.display = 'flex'
  }

  showCheckoutFields() {
    if (!this.hasCheckoutFieldsSectionTarget) return
    this.checkoutFieldsSectionTarget.classList.remove('hidden')
    this.checkoutFieldsSectionTarget.style.display = 'block'
  }

  hideCheckoutFields() {
    if (!this.hasCheckoutFieldsSectionTarget) return
    this.checkoutFieldsSectionTarget.classList.add('hidden')
    this.checkoutFieldsSectionTarget.style.display = 'none'
  }

  // Handle checkout field dependencies (if enabled unchecked -> required unchecked)
  toggleFieldRequired(event) {
    const enabledCheckbox = event.target
    const fieldKey = enabledCheckbox.dataset.fieldKey
    if (!fieldKey) return

    const requiredCheckbox = this.element.querySelector(`input[data-field-required="${fieldKey}"]`)
    if (requiredCheckbox) {
      if (!enabledCheckbox.checked) {
        requiredCheckbox.checked = false
        requiredCheckbox.disabled = true
        requiredCheckbox.parentElement.classList.add('opacity-50')
      } else {
        requiredCheckbox.disabled = false
        requiredCheckbox.parentElement.classList.remove('opacity-50')
      }
    }
  }

  // ============================================
  // ORDER BUMP MANAGEMENT
  // ============================================

  orderBumpToggled(event) {
    const isChecked = event.target.checked
    console.log(`🎁 Order bumps ${isChecked ? 'enabled' : 'disabled'}`)

    if (!this.hasOrderBumpConfigTarget) return

    this.orderBumpConfigTarget.style.display = isChecked ? 'block' : 'none'

    if (!isChecked) {
      this.clearOrderBumpForms()
    }

    console.log(`✅ Order bump visibility updated`)
  }

  addOrderBumpForm(event) {
    event.preventDefault()
    console.log("➕ Adding new order bump form")

    this.clearAllProductErrors()

    const container = document.getElementById('order-bump-forms-container')
    const template = document.getElementById('order-bump-form-template')

    if (!container || !template) {
      console.error("Container or template not found")
      return
    }

    const clone = template.content.cloneNode(true)
    const uniqueId = `new_${Date.now()}`
    const existingForms = container.querySelectorAll('[data-bump-id], [data-bump-index]')
    const displayNumber = existingForms.length + 1

    const formDiv = clone.querySelector('.order-bump-form-group')
    formDiv.setAttribute('data-bump-index', uniqueId)

    const bumpNumbers = clone.querySelectorAll('.bump-number, .text-white.font-bold.text-lg')
    bumpNumbers.forEach(el => el.textContent = displayNumber)

    const productSelect = clone.querySelector('.order-bump-product-select')
    const nameInput = clone.querySelector('.order-bump-name-input')
    const descInput = clone.querySelector('.order-bump-desc-input')

    if (productSelect) productSelect.name = `order_bumps[${uniqueId}][product_id]`
    if (nameInput) nameInput.name = `order_bumps[${uniqueId}][name]`
    if (descInput) descInput.name = `order_bumps[${uniqueId}][description]`

    container.appendChild(clone)
    console.log(`✅ Added order bump #${displayNumber} with ID: ${uniqueId}`)
  }

  removeOrderBumpForm(event) {
    event.preventDefault()
    event.stopPropagation()
    console.log("🗑️ Remove order bump requested")

    const button = event.currentTarget
    const formGroup = button.closest('.order-bump-form-group')

    if (!formGroup) {
      console.error("❌ Form group not found!")
      return
    }

    const bumpId = formGroup.getAttribute('data-bump-id')
    const displayNumber = this.getBumpDisplayNumber(formGroup)

    if (!this.confirmDeletion(displayNumber, bumpId)) {
      console.log("❌ Deletion cancelled")
      return
    }

    console.log(`✅ User confirmed deletion of ${displayNumber}`)

    if (bumpId) {
      this.markForDeletion(formGroup, bumpId)
    } else {
      this.removeFormGroup(formGroup)
    }

    this.updateDisplayNumbers()
  }

  markForDeletion(formGroup, bumpId) {
    formGroup.style.opacity = '0.5'
    formGroup.style.pointerEvents = 'none'
    formGroup.style.transition = 'opacity 0.3s ease'

    const header = formGroup.querySelector('h4')
    if (header && !header.querySelector('.deletion-badge')) {
      const badge = document.createElement('span')
      badge.className = 'deletion-badge ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-beb-danger-soft text-beb-danger-text'
      badge.textContent = '🗑️ Will be deleted'
      header.appendChild(badge)
    }

    this.addDeletionField(bumpId)
    console.log(`✅ Marked bump ${bumpId} for deletion`)
  }

  removeFormGroup(formGroup) {
    formGroup.style.transition = 'opacity 0.3s ease, transform 0.3s ease'
    formGroup.style.opacity = '0'
    formGroup.style.transform = 'scale(0.95)'

    setTimeout(() => {
      formGroup.remove()
      console.log("✅ Removed new bump from DOM")
    }, 300)
  }

  addDeletionField(bumpId) {
    const form = this.element.querySelector('form') || this.element.closest('form')

    if (!form) {
      console.error('❌ Cannot find form element')
      return
    }

    if (form.querySelector(`input[name="delete_order_bump_ids[]"][value="${bumpId}"]`)) {
      console.log(`✅ Deletion field already exists for bump ${bumpId}`)
      return
    }

    const deleteField = document.createElement('input')
    deleteField.type = 'hidden'
    deleteField.name = 'delete_order_bump_ids[]'
    deleteField.value = bumpId
    deleteField.setAttribute('data-deletion-field', 'true')
    form.appendChild(deleteField)

    console.log(`✅ Added deletion field for bump ${bumpId}`)
  }

  confirmDeletion(displayNumber, bumpId) {
    const productName = bumpId ? this.getBumpProductName(bumpId) : null

    const message = `Are you sure you want to delete ${displayNumber}${productName ? ` (${productName})` : ''}?\n\n` +
      `⚠️ This will permanently remove this order bump.\n\n` +
      `This action cannot be undone.`

    return confirm(message)
  }

  getBumpDisplayNumber(formGroup) {
    const header = formGroup.querySelector('h4')
    if (header) {
      const match = header.textContent.match(/\d+/)
      return match ? `Order Bump ${match[0]}` : 'Order Bump'
    }
    return 'Order Bump'
  }

  getBumpProductName(bumpId) {
    const formGroup = document.querySelector(`[data-bump-id="${bumpId}"]`)
    if (formGroup) {
      const productSelect = formGroup.querySelector('select[name*="product_id"]')
      if (productSelect?.selectedOptions.length > 0 && productSelect.value) {
        return productSelect.selectedOptions[0].text
      }
    }
    return null
  }

  updateDisplayNumbers() {
    const container = document.getElementById('order-bump-forms-container')
    if (!container) return

    const forms = container.querySelectorAll('.order-bump-form-group')

    forms.forEach((form, index) => {
      const displayNumber = index + 1
      const bumpNumbers = form.querySelectorAll('.bump-number, .text-white.font-bold.text-lg')
      bumpNumbers.forEach(el => el.textContent = displayNumber)

      if (!form.hasAttribute('data-bump-id')) {
        const header = form.querySelector('h4')
        if (header && !form.querySelector('.deletion-badge')) {
          const productName = this.getProductNameFromForm(form)
          header.textContent = productName || `Order Bump ${displayNumber}`
        }
      }
    })

    console.log(`✅ Updated display numbers for ${forms.length} bumps`)
  }

  getProductNameFromForm(formGroup) {
    const select = formGroup.querySelector('select[name*="product_id"]')
    if (select?.selectedOptions.length > 0 && select.value) {
      return select.selectedOptions[0].text
    }
    return null
  }

  clearOrderBumpForms() {
    const container = document.getElementById('order-bump-forms-container')
    if (!container) return

    this.clearAllProductErrors()

    container.querySelectorAll('select, input, textarea').forEach(field => {
      if (field.closest('#order-bump-form-template')) return

      if (field.tagName === 'SELECT') {
        field.selectedIndex = 0
      } else {
        field.value = ''
      }
    })

    console.log("✅ Cleared all order bump form values")
  }

  // ============================================
  // PRODUCT DUPLICATION VALIDATION
  // ============================================

  handleProductChange(event) {
    const changedSelect = event.target
    const selectedProductId = changedSelect.value

    this.clearProductError(changedSelect)

    if (!selectedProductId) return

    const container = document.getElementById('order-bump-forms-container')
    const allForms = container.querySelectorAll('[data-bump-id], [data-bump-index]')
    const changedForm = changedSelect.closest('[data-bump-id], [data-bump-index]')

    let duplicateForm = null

    allForms.forEach(form => {
      if (form === changedForm) return

      const select = form.querySelector('select[name*="product_id"]')
      if (select?.value === selectedProductId) {
        duplicateForm = form
      }
    })

    if (duplicateForm) {
      const duplicateDisplayNumber = this.getBumpDisplayNumber(duplicateForm)
      this.showProductError(changedSelect, duplicateDisplayNumber)
      changedSelect.value = ''
      changedSelect.focus()
    }
  }

  showProductError(selectElement, duplicateInfo) {
    const productDiv = selectElement.closest('div')

    this.clearProductError(selectElement)

    const errorDiv = document.createElement('div')
    errorDiv.className = 'product-error-message mt-2 p-3 bg-beb-danger-soft border border-beb-danger rounded-lg flex items-start gap-2 text-sm text-beb-danger-text'
    errorDiv.innerHTML = `
      <span class="text-base flex-shrink-0">⚠️</span>
      <span><strong>Duplicate Product:</strong> This product is already used in ${duplicateInfo}. Each product can only be used once.</span>
    `

    productDiv.appendChild(errorDiv)

    setTimeout(() => {
      if (errorDiv.parentNode) errorDiv.remove()
    }, 8000)

    console.log(`❌ Duplicate product detected: ${duplicateInfo}`)
  }

  clearProductError(selectElement) {
    const productDiv = selectElement.closest('div')
    const existingError = productDiv?.querySelector('.product-error-message')
    existingError?.remove()
  }

  clearAllProductErrors() {
    const container = document.getElementById('order-bump-forms-container')
    if (!container) return

    const errors = container.querySelectorAll('.product-error-message')
    errors.forEach(error => error.remove())

    if (errors.length > 0) {
      console.log(`✅ Cleared ${errors.length} error messages`)
    }
  }

  // ============================================
  // 🆕 NEW: SUCCESS REDIRECT HANDLING
  // ============================================

  successRedirectTypeChanged(event) {
    const selectedType = event.target.value
    console.log(`🔄 Redirect type changed to: ${selectedType}`)

    // Hide all conditional sections
    if (this.hasCustomSuccessUrlInputTarget) {
      this.customSuccessUrlInputTarget.style.display = 'none'
    }
    if (this.hasSpecificStepSelectorTarget) {
      this.specificStepSelectorTarget.style.display = 'none'
    }
    if (this.hasInternalPageRedirectSelectorTarget) {
      this.internalPageRedirectSelectorTarget.style.display = 'none'
    }

    // Show the appropriate section
    switch (selectedType) {
      case 'custom_url':
        if (this.hasCustomSuccessUrlInputTarget) {
          this.customSuccessUrlInputTarget.style.display = 'block'
          const input = this.customSuccessUrlInputTarget.querySelector('input')
          if (input) input.required = true
        }
        break

      case 'specific_step':
        if (this.hasSpecificStepSelectorTarget) {
          this.specificStepSelectorTarget.style.display = 'block'
        }
        break

      case 'internal_page':
        if (this.hasInternalPageRedirectSelectorTarget) {
          this.internalPageRedirectSelectorTarget.style.display = 'block'
        }
        break

      default:
        // next_step or password_setup - no additional fields needed
        break
    }
  }

  // ============================================
  // FORM SUBMISSION
  // ============================================

  beforeSubmit(event) {
    console.log("📤 Form submitting...")
    console.log("Order bump checkbox checked:", this.orderBumpCheckboxTarget?.checked)

    if (this.orderBumpCheckboxTarget && !this.orderBumpCheckboxTarget.checked) {
      console.log("Order bumps disabled - clearing field values")

      const container = document.getElementById('order-bump-forms-container')
      if (container) {
        container.querySelectorAll('input, select, textarea').forEach(field => {
          if (field.closest('#order-bump-form-template')) return

          field.removeAttribute('required')
          if (field.tagName === 'SELECT') {
            field.selectedIndex = 0
          } else {
            field.value = ''
          }
        })
      }
    }

    const allInputs = this.element.querySelectorAll('input:disabled, select:disabled, textarea:disabled')

    allInputs.forEach(input => {
      if (input.closest('#order-bump-form-template')) {
        return
      }

      input.disabled = false
      console.log(`Re-enabled for submission: ${input.name || input.id}`)
    })

    const orderBumpFields = this.element.querySelectorAll('[name^="order_bumps"]')
    const enabledOrderBumpFields = Array.from(orderBumpFields).filter(f => !f.disabled)
    console.log(`📊 Submitting ${enabledOrderBumpFields.length} order bump fields`)

    enabledOrderBumpFields.forEach(field => {
      if (field.name && field.value) {
        console.log(`  ✅ ${field.name} = ${field.value}`)
      }
    })

    console.log("✅ Form prepared for submission")
    return true
  }
}
